/* eslint-disable no-param-reassign */
import PadletApi from 'surface/padlet_api'
import { captureException } from 'bits/error_tracker'

export default {
  namespaced: true,
  state: {
    commentsFetched: false,
    commentEntities: {},
    pendingCommentIds: [],
    commentBeingEditedId: null,
    actionMenuCommentId: null
  },
  getters: {
    commentIds: state => {
      return Object.keys(state.commentEntities)
    },
    commentIdsByPost: (state, getters) => {
      let commentsByPost = {}
      getters.commentIds.forEach(rid => {
        let r = state.commentEntities[rid]
        commentsByPost[r.wish_id] = commentsByPost[r.wish_id] || []
        commentsByPost[r.wish_id].push(rid)
      })
      return commentsByPost
    }
  },
  mutations: {
    FETCH_COMMENTS_SUCCESS (state, comments) {
      if (comments) {
        let newCommentEntities = {}
        comments.forEach(r => {
          newCommentEntities[r.id] = r
        })
        state.commentEntities = newCommentEntities // Trigger state change once
        state.pendingCommentIds = []
      }
      state.commentsFetched = true
    },
    UPDATE_COMMENT (state, { id, obj }) {
      if (id < 0 || !obj) {
        return
      }
      let updatedEntity = {
        ...state.commentEntities[id],
        ...obj
      }
      state.commentEntities = {
        ...state.commentEntities,
        [id]: updatedEntity
      }
    },
    ADD_PENDING_COMMENT (state, pendingComment) {
      let tempId = 'new_' + new Date().valueOf()
      state.commentEntities = {
        ...state.commentEntities,
        [tempId]: {
          ...pendingComment,
          id: tempId
        }
      }
      state.pendingCommentIds.push(tempId)
    },
    ADD_COMMENT (state, comment) {
      let pendingCommentIdRemoved = state.pendingCommentIds.some(tempId => {
        if ((state.commentEntities[tempId].wish_id === comment.wish_id) &&
          (state.commentEntities[tempId].body === comment.body) &&
          (state.commentEntities[tempId].user.id === comment.user.id)) {
          let newCommentEntities = { ...state.commentEntities }
          // delete pending comment
          delete newCommentEntities[tempId]
          // put new comment in place of pending comment
          state.commentEntities = {
            ...newCommentEntities,
            [comment.id]: comment
          }
          state.pendingCommentIds = state.pendingCommentIds.filter(i => i !== tempId)
          return true
        }
        return false
      })
      if (!pendingCommentIdRemoved) {
        state.commentEntities = {
          ...state.commentEntities,
          [comment.id]: comment
        }
      }
    },
    REMOVE_COMMENT (state, id) {
      let newCommentEntities = { ...state.commentEntities }
      delete newCommentEntities[id]
      state.commentEntities = newCommentEntities
    },
    EDIT_COMMENT_REMOTE (state, comment) {
      let originalComment = state.commentEntities[comment.id]
      // real time can be out of sync sometimes.
      // here we make sure we are not updating a new copy with a stale one
      if (originalComment && originalComment.updated_at <= comment.updated_at) {
        state.commentEntities = {
          ...state.commentEntities,
          [comment.id]: comment
        }
      }
    },
    START_EDITING_COMMENT (state, commentId) {
      state.commentBeingEditedId = commentId
    },
    STOP_EDITING_COMMENT (state) {
      state.commentBeingEditedId = null
    },
    SHOW_COMMENT_ACTION_MENU (state, commentId) {
      if (state.actionMenuCommentId) {
        state.actionMenuCommentId = null
      } else {
        state.actionMenuCommentId = commentId
      }
    },
    HIDE_COMMENT_ACTION_MENU (state) {
      state.actionMenuCommentId = null
    }
  },
  actions: {
    /* ------------
     * CRUD
     * ------------ */
    fetchComments ({ dispatch, rootState }) {
      PadletApi.Comment.readAll({ wall: { id: rootState.wall.id } })
        .then((comments) => {
          dispatch('fetchCommentsSuccess', { comments })
        }).catch(captureException)
    },
    fetchCommentsSuccess ({ commit }, { comments }) {
      commit('FETCH_COMMENTS_SUCCESS', comments)
    },
    createComment ({
      commit, state, rootState, dispatch
    }, { postId, body, createdAt }) {
      let newComment = {
        wish_id: postId,
        body: body,
        created_at: createdAt
      }
      // not handling pending comment for now, request is quick enough
      commit('ADD_PENDING_COMMENT', {
        ...newComment,
        user: rootState.user,
        user_id: rootState.user.id
      })
      PadletApi.Comment.create(newComment)
        .then((comment) => {
          dispatch('createCommentSuccess', { comment })
        })
    },
    createCommentSuccess ({ commit }, { comment }) {
      commit('ADD_COMMENT', comment)
    },
    deleteComment ({ commit, state }, { commentId }) {
      const existingComment = state.commentEntities[commentId]
      commit('STOP_EDITING_COMMENT')
      if (existingComment) {
        commit('REMOVE_COMMENT', existingComment.id)
        PadletApi.Comment.delete(existingComment)
      }
    },
    startEditingComment ({ commit }, { commentId }) {
      commit('START_EDITING_COMMENT', commentId)
    },
    stopEditingComment ({ commit }) {
      commit('STOP_EDITING_COMMENT')
    },
    editComment ({ commit }, { id, body }) {
      PadletApi.Comment.update({ id, body })
      commit('UPDATE_COMMENT', { id: id, obj: { body } })
      commit('STOP_EDITING_COMMENT')
    },

    /* ------------
     * REMOTE
     * ------------ */
    newCommentRemote ({ commit }, payload) {
      commit('ADD_COMMENT', payload)
    },
    editCommentRemote ({ commit }, payload) {
      commit('EDIT_COMMENT_REMOTE', payload)
    },
    deleteCommentRemote ({ commit, state }, comment) {
      if (state.commentBeingEditedId && state.commentBeingEditedId === comment.id) {
        commit('STOP_EDITING_COMMENT')
      }
      if (state.actionMenuCommentId && state.actionMenuCommentId === comment.id) {
        commit('HIDE_COMMENT_ACTION_MENU')
      }
      commit('REMOVE_COMMENT', comment.id)
    },

    /* ------------
    * ACTION MENU
    * ------------ */
    showCommentActionMenu ({ commit }, { commentId }) {
      commit('SHOW_COMMENT_ACTION_MENU', commentId)
    },
    hideCommentActionMenu ({ commit }) {
      commit('HIDE_COMMENT_ACTION_MENU')
    }
  }
}
